import React, { useMemo } from "react";
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";

function labelObjective(v) {
  const map = {
    gerar_leads: "Gerar leads",
    vender_mais: "Vender mais",
    recuperar_clientes: "Recuperar clientes",
  };
  return map[v] || v || null;
}

function labelChannel(v) {
  const map = {
    instagram: "Instagram",
    whatsapp: "WhatsApp",
  };
  const raw = (v || "").toString().trim();
  if (!raw) return null;
  return map[raw] || raw;
}

function formatMetric(value, kind) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n)) {
    if (kind === "revenue") {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(n);
    }
    return new Intl.NumberFormat("pt-BR").format(n);
  }
  return String(value);
}

export default function CampaignCard({ campaign, onClick, onToggleStatus }) {
  const objective = useMemo(() => labelObjective(campaign?.objective), [campaign?.objective]);
  const channel = useMemo(
    () => labelChannel((campaign?.channels?.[0] || campaign?.channel || "").toString()),
    [campaign?.channels, campaign?.channel]
  );
  const status = campaign?.status || "paused";

  const leads = campaign?.leads ?? campaign?.metrics?.leads ?? null;
  const conversions = campaign?.conversions ?? campaign?.metrics?.conversions ?? null;
  const revenue = campaign?.revenue ?? campaign?.metrics?.revenue ?? null;

  const name = campaign?.name?.trim() || "Campanha sem nome";
  const leadsStr = formatMetric(leads, "count");
  const convStr = formatMetric(conversions, "count");
  const revStr = formatMetric(revenue, "revenue");

  const metricSlots = [
    { label: "Leads", value: leadsStr, hint: "Captações vinculadas a esta campanha" },
    { label: "Conversões", value: convStr, hint: "Vendas ou metas concluídas" },
    { label: "Receita", value: revStr, hint: "Valor estimado das conversões" },
  ];

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { borderColor: "primary.main", boxShadow: 1 },
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
    >
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={name} enterDelay={400} disableHoverListener={name.length < 48}>
              <Typography
                component="h3"
                fontWeight={800}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                }}
              >
                {name}
              </Typography>
            </Tooltip>
            <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" useFlexGap>
              {objective ? <Chip size="small" label={objective} variant="outlined" color="primary" /> : null}
              {channel ? <Chip size="small" label={channel} variant="outlined" /> : null}
              <Chip
                size="small"
                label={status === "active" ? "Ativa" : "Pausada"}
                color={status === "active" ? "success" : "default"}
                variant={status === "active" ? "filled" : "outlined"}
              />
            </Stack>
          </Box>
          <Button
            size="small"
            variant="outlined"
            sx={{ flexShrink: 0, alignSelf: "flex-start" }}
            aria-label={status === "active" ? "Pausar campanha" : "Ativar campanha"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus?.();
            }}
          >
            {status === "active" ? "Pausar" : "Ativar"}
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={0} justifyContent="space-between" flexWrap="wrap" useFlexGap>
          {metricSlots.map(({ label, value, hint }) => (
            <Tooltip key={label} title={hint} arrow placement="top">
              <Box sx={{ flex: "1 1 88px", minWidth: 88 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                  {label}
                </Typography>
                {value ? (
                  <Typography fontWeight={800} variant="body1" component="p" sx={{ m: 0 }}>
                    {value}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                    Sem dados
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

